import { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Bold, Italic, List, ListOrdered, Heading1, Heading2, Link as LinkIcon,
  Code, Quote, Eye, Pencil, SplitSquareHorizontal, Sparkles } from "lucide-react";
import { WikiAiDialog } from "@/components/WikiAiDialog";

/**
 * MarkdownEditor — small toolbar + textarea + optional live preview column.
 * Uses react-markdown + remark-gfm for GFM support (tables, task lists).
 */
export function MarkdownEditor({ value, onChange, rows = 14, dataTestId = "md-editor",
                                  aiContext }) {
  const [mode, setMode] = useState("split"); // 'edit' | 'preview' | 'split'
  const [aiOpen, setAiOpen] = useState(false);

  const wrap = (before, after = "") => {
    const el = document.querySelector(`[data-testid="${dataTestId}-textarea"]`);
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const chunk = value.slice(start, end);
    const next = value.slice(0, start) + before + chunk + after + value.slice(end);
    onChange(next);
    setTimeout(() => {
      el.focus();
      el.selectionStart = start + before.length;
      el.selectionEnd = end + before.length;
    }, 10);
  };

  const insertLine = (prefix) => {
    const el = document.querySelector(`[data-testid="${dataTestId}-textarea"]`);
    if (!el) return;
    const start = el.selectionStart;
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    onChange(value.slice(0, lineStart) + prefix + value.slice(lineStart));
  };

  return (
    <div data-testid={dataTestId}>
      <div className="flex items-center gap-1 mb-2 p-1 rounded-md bg-white/[0.03] border border-white/10">
        <ToolButton onClick={() => wrap("**", "**")} icon={Bold} testid="md-bold" title="Bold" />
        <ToolButton onClick={() => wrap("_", "_")} icon={Italic} testid="md-italic" title="Italic" />
        <div className="w-px h-4 bg-white/10 mx-1" />
        <ToolButton onClick={() => insertLine("# ")} icon={Heading1} testid="md-h1" title="Heading 1" />
        <ToolButton onClick={() => insertLine("## ")} icon={Heading2} testid="md-h2" title="Heading 2" />
        <ToolButton onClick={() => insertLine("- ")} icon={List} testid="md-ul" title="Bulleted list" />
        <ToolButton onClick={() => insertLine("1. ")} icon={ListOrdered} testid="md-ol" title="Ordered list" />
        <ToolButton onClick={() => insertLine("> ")} icon={Quote} testid="md-quote" title="Quote" />
        <ToolButton onClick={() => wrap("`", "`")} icon={Code} testid="md-code" title="Code" />
        <ToolButton onClick={() => wrap("[", "](https://)")} icon={LinkIcon} testid="md-link" title="Link" />
        {aiContext && (
          <>
            <div className="w-px h-4 bg-white/10 mx-1" />
            <button
              type="button"
              onClick={() => setAiOpen(true)}
              title="AI Draft — Claude Sonnet 4.5"
              data-testid="md-ai-btn"
              className="inline-flex items-center gap-1 h-7 px-2 rounded text-xs bg-orange-500/10 text-orange-300 hover:bg-orange-500/20 transition"
            >
              <Sparkles className="h-3 w-3" /> AI draft
            </button>
          </>
        )}
        <div className="ml-auto flex items-center gap-1">
          <ToolButton onClick={() => setMode("edit")} icon={Pencil} testid="md-mode-edit"
                      title="Edit only" active={mode === "edit"} />
          <ToolButton onClick={() => setMode("split")} icon={SplitSquareHorizontal} testid="md-mode-split"
                      title="Split view" active={mode === "split"} />
          <ToolButton onClick={() => setMode("preview")} icon={Eye} testid="md-mode-preview"
                      title="Preview only" active={mode === "preview"} />
        </div>
      </div>
      <div className={mode === "split" ? "grid grid-cols-2 gap-3" : ""}>
        {mode !== "preview" && (
          <Textarea rows={rows} value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Write in **Markdown** — try `# Heading`, `- bullets`, `[link](url)`, tables, or > quotes"
                    className="bg-white/[0.04] border-white/10 font-mono text-xs"
                    data-testid={`${dataTestId}-textarea`} />
        )}
        {mode !== "edit" && (
          <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 text-sm prose prose-invert prose-sm max-w-none overflow-y-auto"
               style={{ maxHeight: `${rows * 1.5}em` }}
               data-testid={`${dataTestId}-preview`}>
            {value ? (
              <MarkdownPreview value={value} />
            ) : (
              <span className="text-zinc-500 italic">Nothing to preview yet — start typing on the left.</span>
            )}
          </div>
        )}
      </div>
      {aiContext && (
        <WikiAiDialog
          open={aiOpen}
          onOpenChange={setAiOpen}
          currentBody={value}
          currentTitle={aiContext.title}
          currentTags={aiContext.tags}
          onAccept={(md) => onChange(md)}
        />
      )}
    </div>
  );
}

const md_components = {
  a: (props) => <a {...props} target="_blank" rel="noreferrer" className="text-orange-400 hover:underline" />,
  h1: (props) => <h1 {...props} className="text-2xl font-display font-semibold mt-3 mb-2 text-zinc-100" />,
  h2: (props) => <h2 {...props} className="text-xl font-display font-semibold mt-3 mb-2 text-zinc-100" />,
  h3: (props) => <h3 {...props} className="text-lg font-display font-semibold mt-2 mb-1 text-zinc-100" />,
  code: (props) => <code {...props} className="bg-white/[0.06] rounded px-1 py-0.5 text-orange-300 text-[0.85em]" />,
  blockquote: (props) => <blockquote {...props} className="border-l-2 border-orange-500/40 pl-3 text-zinc-400 italic my-2" />,
  table: (props) => <table {...props} className="w-full border-collapse text-xs my-2" />,
  th: (props) => <th {...props} className="border border-white/10 bg-white/[0.04] px-2 py-1 text-left text-zinc-200" />,
  td: (props) => <td {...props} className="border border-white/10 px-2 py-1 text-zinc-300" />,
  li: (props) => <li {...props} className="text-zinc-300" />,
  p: (props) => <p {...props} className="text-zinc-300 my-1.5 leading-relaxed" />,
};

/**
 * MarkdownPreview — read-only renderer. Use standalone (not just inside editor).
 */
export function MarkdownPreview({ value, className = "prose prose-invert prose-sm max-w-none" }) {
  const trimmed = useMemo(() => (value || "").trim(), [value]);
  if (!trimmed) return null;
  return (
    <div className={className} data-testid="md-preview">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={md_components}>
        {trimmed}
      </ReactMarkdown>
    </div>
  );
}

function ToolButton({ onClick, icon: Icon, title, testid, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={"inline-flex items-center justify-center h-7 w-7 rounded transition " +
        (active ? "bg-orange-500/20 text-orange-300"
                : "text-zinc-400 hover:text-white hover:bg-white/5")}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}
